import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AdminController, AdminUser } from '../../controllers/admin'
import UserLookup from '../components/UserLookup'

export default function SignInLinkPage() {
  const { t } = useTranslation()
  const [user, setUser] = React.useState<AdminUser | null>(null)
  const [link, setLink] = React.useState<{ url: string; expires_at: string } | null>(null)
  const [loading, setLoading] = React.useState(false)

  const loadUser = async (id: string) => {
    setLoading(true)
    setUser(null)
    setLink(null)
    try {
      setUser(await AdminController.getUser(id))
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const generate = async () => {
    setLoading(true)
    try {
      setLink(await AdminController.createSignInLink(user.id))
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const copy = async () => {
    await navigator.clipboard.writeText(link.url)
    toast.success(t('admin::Link copied'))
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-xl font-semibold">{t('admin::Sign-in link')}</h1>
      <UserLookup loading={loading} onSelect={loadUser} />

      {user && (
        <Card>
          <CardHeader>
            <CardTitle>{user.email}</CardTitle>
            <CardDescription>{user.name}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {user.admin ? (
              <p className="text-sm text-muted-foreground">{t('admin::Sign-in links cannot be created for admin users.')}</p>
            ) : (
              <Button className="self-start" onClick={generate} disabled={loading}>
                {t('admin::Generate link')}
              </Button>
            )}
            {link && (
              <>
                <div className="flex gap-2">
                  <Input readOnly value={link.url} onFocus={(e) => e.target.select()} />
                  <Button variant="outline" size="icon" onClick={copy} aria-label={t('admin::Copy')}>
                    <Copy />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('admin::Expires at {{time}}', { time: new Date(link.expires_at).toLocaleTimeString() })}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
